import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  List,
  ListItem,
  Notice,
  Skeleton,
  iconChevron,
  useSnackbar,
} from '@sauda/ui/kit';
import { useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { formatRupees } from '../lib/money.js';
import { formatCalendarDate } from '../lib/time.js';

export interface OwingLine {
  dealId: string;
  party: 'tenant' | 'owner';
  name: string;
  propertyTitle: string;
  amountPaise: number;
  dueOn: string | null;
  overdue: boolean;
  canNudge: boolean;
}

/** "Mr Kulkarni owes ₹14,000": each person by name, with a one tap reminder that carries the amount. */
export function OwingList({ limit }: { limit?: number }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const owing = useQuery({
    queryKey: ['owing', auth.agencyId],
    queryFn: () => api<OwingLine[]>('/passbook/owing'),
    enabled: auth.allowed('passbook.read'),
  });
  const nudge = useMutation({
    mutationFn: (line: OwingLine) =>
      api<{ sent: boolean }>(`/deals/${line.dealId}/brokerage-nudge`, {
        method: 'POST',
        body: { party: line.party },
      }),
    onSuccess: (result) => {
      snack({ text: t(result.sent ? 'owing.sent' : 'owing.alreadySent') });
      void cache.invalidateQueries({ queryKey: ['owing'] });
    },
    onError: (e) =>
      snack({
        text:
          e instanceof ApiError && e.status < 500 && e.message !== ''
            ? e.message
            : t('owing.failed'),
      }),
  });
  if (!auth.allowed('passbook.read')) return null;
  if (owing.isPending) return <Skeleton height={72} />;
  if (owing.isError) return <Notice tone="error">{t('today.loadFailed')}</Notice>;
  const lines = limit === undefined ? owing.data : owing.data.slice(0, limit);
  if (lines.length === 0) return <p className="k-t-body k-muted">{t('owing.none')}</p>;
  const canRemind = auth.allowed('deals.run') && !chrome.readOnly;
  return (
    <List label={t('owing.title')}>
      {lines.map((l) => (
        <ListItem
          key={`${l.dealId}-${l.party}`}
          title={t('owing.line', { name: l.name, amount: formatRupees(l.amountPaise) })}
          supporting={[
            l.propertyTitle,
            l.dueOn === null
              ? t('passbook.noDueDate')
              : `${t('passbook.dueOn')} ${formatCalendarDate(l.dueOn)}${l.overdue ? ` · ${t('passbook.overdue')}` : ''}`,
          ].join(' · ')}
          onClick={() => void navigate(`/deals/${l.dealId}`)}
          action={
            canRemind && l.canNudge ? (
              <Button
                variant="tonal"
                loading={nudge.isPending && nudge.variables === l}
                onClick={() => nudge.mutate(l)}
              >
                {t('owing.remind')}
              </Button>
            ) : undefined
          }
          trailing={<Icon icon={iconChevron} />}
        />
      ))}
    </List>
  );
}
